import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, Input, Modal, Popconfirm, Skeleton, Switch, Table, Tag, Typography, App } from 'antd';

import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import useDateFormat from '../../hooks/useDateFormat';
import { MAX_CONNECTION_NAME, grantAccessLabel, listGrants, renameGrant, revokeGrant } from '../../services/oauth';
import { destructivePopconfirmProps } from '../common/confirmDestructive';
import { useCollections } from '../../contexts/CollectionsContext';

const { Text } = Typography;

const STATUS_TAGS = {
	active: { color: 'green', label: 'Connected' },
	expired: { color: 'default', label: 'Expired' },
	revoked: { color: 'red', label: 'Disconnected' },
};

// Names shown inline under "N collections" before it turns into "+k more".
const MAX_NAMES_INLINE = 3;

/**
 * "Connected assistants" on the account page: the OAuth grants this person
 * has given — Claude on the web, ChatGPT, Claude Code — each a key by
 * another name (read or read-and-write, every collection or a chosen few,
 * never admin), made on the consent screen rather than in a form here.
 * There is no create button on purpose: an assistant is connected FROM the
 * assistant, and the person only ever sees the consent screen. Disconnect
 * is immediate and cannot be undone — hence the confirm.
 *
 * One assistant can be connected more than once (two Claude accounts), so
 * each connection carries the person's own name for it — chosen on the
 * consent screen, changed here with Rename. When that name is not the
 * assistant's own, the assistant's name is shown under it.
 *
 * Exported default and not yet mounted: Profile.js mounts it beside the API
 * keys panel.
 */
export default function ConnectedAssistantsPanel() {
	const { message } = App.useApp();
	useDateFormat();

	const [grants, setGrants] = useState(null);
	const [loadError, setLoadError] = useState(null);
	const [showRevoked, setShowRevoked] = useState(false);
	const { collections } = useCollections();
	// The connection being renamed ({ id, name }), and the draft name.
	const [renaming, setRenaming] = useState(null);
	const [draftName, setDraftName] = useState('');
	const [savingName, setSavingName] = useState(false);
	const nameInputRef = useRef(null);

	const load = useCallback(() => {
		let active = true;
		listGrants({ includeRevoked: showRevoked })
			.then(rows => { if (active) { setGrants(rows); setLoadError(null); } })
			.catch(error => { if (active) setLoadError(error); });
		return () => { active = false; };
	}, [showRevoked]);

	useEffect(load, [load]);


	const collectionNameById = useMemo(() => {
		const map = {};
		collections.forEach(collection => {
			const id = normalizeId(collection?._id);
			if (id) map[id] = collection?.name || id;
		});
		return map;
	}, [collections]);

	const handleDisconnect = useCallback(async (id) => {
		try {
			await revokeGrant(id);
			message.success('Assistant disconnected.');
		} catch (error) {
			message.error(error?.response?.data?.message || 'Could not disconnect the assistant.');
		}
		load();
	}, [load, message]);

	const openRename = useCallback((row) => {
		const current = row.name || row.clientName || '';
		setRenaming({ id: row.id, name: current });
		setDraftName(current);
	}, []);

	const handleRename = useCallback(async () => {
		const name = draftName.trim();
		if (!renaming || !name) return;
		setSavingName(true);
		try {
			const updated = await renameGrant(renaming.id, name);
			setGrants(rows => (rows || []).map(row => (row.id === renaming.id ? { ...row, ...updated } : row)));
			setRenaming(null);
		} catch (error) {
			message.error(error?.response?.data?.message || 'Could not rename the assistant.');
		} finally {
			setSavingName(false);
		}
	}, [draftName, renaming, message]);

	const renderCollections = (collectionIds) => {
		if (!Array.isArray(collectionIds) || collectionIds.length === 0) return 'All collections';
		const names = collectionIds.map(id => collectionNameById[normalizeId(id)]).filter(Boolean);
		const shown = names.slice(0, MAX_NAMES_INLINE);
		const more = names.length - shown.length;
		return (
			<>
				{collectionIds.length === 1 ? '1 collection' : `${collectionIds.length} collections`}
				{shown.length ? (
					<span className="connected-assistants__collections-names" title={names.join(', ')}>
						{shown.join(', ')}{more > 0 ? ` +${more} more` : ''}
					</span>
				) : null}
			</>
		);
	};

	const columns = [
		{
			title: 'Assistant',
			dataIndex: 'name',
			key: 'name',
			render: (name, row) => {
				const host = row.clientKind === 'cimd' && row.clientId ? safeHost(row.clientId) : '';
				const client = name && row.clientName && name !== row.clientName ? row.clientName : '';
				return (
					<>
						<span className="connected-assistants__name">{name || row.clientName}</span>
						{client || host ? (
							<span className="connected-assistants__client">{[client, host].filter(Boolean).join(' · ')}</span>
						) : null}
					</>
				);
			},
		},
		{
			title: 'Access',
			key: 'scopes',
			width: 150,
			render: (_, row) => <Tag>{grantAccessLabel(row)}</Tag>,
		},
		{
			title: 'Collections',
			dataIndex: 'collectionIds',
			key: 'collections',
			render: renderCollections,
		},
		{
			title: 'Connected',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 130,
			render: value => <span className="connected-assistants__meta">{value ? formatDisplayDate(value) : '—'}</span>,
		},
		{
			title: 'Last used',
			dataIndex: 'lastUsedAt',
			key: 'lastUsedAt',
			width: 160,
			render: value => <span className="connected-assistants__meta">{value ? formatDisplayDateTime(value) : 'never'}</span>,
		},
		{
			title: 'Status',
			key: 'status',
			width: 120,
			render: (_, row) => {
				const status = STATUS_TAGS[row.status] || STATUS_TAGS.active;
				return <Tag color={status.color}>{status.label}</Tag>;
			},
		},
		{
			key: 'actions',
			width: 180,
			align: 'right',
			render: (_, row) => (row.status === 'revoked' ? null : (
				<span className="connected-assistants__actions">
					<Button type="text" size="small" onClick={() => openRename(row)} aria-label={`Rename ${row.name || row.clientName}`}>
						Rename
					</Button>
					<Popconfirm
						title="Disconnect this assistant?"
						description="It stops working immediately. To use it again, connect it from the assistant."
						{...destructivePopconfirmProps('Disconnect')}
						onConfirm={() => handleDisconnect(row.id)}
					>
						<Button type="text" danger size="small" aria-label={`Disconnect ${row.name || row.clientName}`}>
							Disconnect
						</Button>
					</Popconfirm>
				</span>
			)),
		},
	];

	let body;
	if (loadError) {
		body = (
			<Alert
				type="error"
				showIcon
				message="Could not load your connected assistants."
				action={<Button size="small" onClick={load}>Retry</Button>}
			/>
		);
	} else if (grants === null) {
		body = <Skeleton active paragraph={{ rows: 2 }} />;
	} else if (grants.length === 0) {
		body = (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={(
					<div className="connected-assistants__empty">
						<strong>No assistants are connected.</strong>
						<p className="mb-0 mt-2">When you connect Claude or ChatGPT to keepr, it appears here.</p>
					</div>
				)}
			/>
		);
	} else {
		body = (
			<div className="connected-assistants__table">
				<Table
					size="small"
					rowKey="id"
					columns={columns}
					dataSource={grants}
					pagination={false}
					scroll={{ x: true }}
				/>
			</div>
		);
	}

	return (
		<section className="connected-assistants">
			<h3>Connected assistants</h3>
			<Text type="secondary" className="connected-assistants__intro">
				Assistants you have connected to keepr — Claude, ChatGPT and others. Each acts as you,
				with the access you allowed on its consent screen, and never as an administrator.
			</Text>

			<div className="connected-assistants__toolbar">
				<label className="connected-assistants__toggle">
					<Switch size="small" checked={showRevoked} onChange={setShowRevoked} />
					Show disconnected
				</label>
			</div>

			{body}

			<Modal
				open={!!renaming}
				title="Rename connection"
				okText="Save"
				onOk={handleRename}
				onCancel={() => setRenaming(null)}
				confirmLoading={savingName}
				okButtonProps={{ disabled: !draftName.trim() }}
				// The modal takes focus as it opens; hand it to the name.
				afterOpenChange={open => { if (open) nameInputRef.current?.focus({ cursor: 'all' }); }}
				destroyOnHidden
			>
				<label className="connected-assistants__rename-label" htmlFor="connected-assistant-name">Name</label>
				<Input
					id="connected-assistant-name"
					value={draftName}
					maxLength={MAX_CONNECTION_NAME}
					onChange={event => setDraftName(event.target.value)}
					onPressEnter={handleRename}
					ref={nameInputRef}
				/>
				<p className="connected-assistants__rename-hint">Only the name changes. What it may do stays as you allowed it.</p>
			</Modal>
		</section>
	);
}

/** The host of a client ID metadata document URL — the honest name of a self-asserted client. */
function safeHost(clientId) {
	try { return new URL(clientId).hostname; } catch { return ''; }
}
